import { useState } from "react";
import { ChevronDown, ChevronUp } from "../../Components/ui/icons";
import {
  PageContainer,
  PageTitle,
  SectionsContainer,
  Section,
  SectionLabel,
  SectionTitle,
  SectionDescription,
  AccordionList,
  AccordionItem,
  AccordionHeader,
  AccordionItemTitle,
  AccordionBody,
} from "../FAQ/styles";

type AccordionEntry = {
  title: string;
  content: string;
};

const MAPA_ITEMS: AccordionEntry[] = [
  {
    title: "Como busco um município ou localização no mapa?",
    content:
      "Use a barra de busca no mapa. Você pode digitar o nome de um município ou inserir coordenadas geográficas no formato lat, lon. O mapa centraliza automaticamente na localização encontrada.",
  },
  {
    title: "Posso buscar por coordenadas?",
    content:
      "Sim. Digite as coordenadas separadas por vírgula (ex: -8.05, -34.88). A plataforma também detecta automaticamente se as coordenadas foram inseridas em ordem invertida.",
  },
  {
    title: "Por que não consigo buscar localizações fora de Pernambuco?",
    content:
      "A plataforma é focada em análises do estado de Pernambuco. Buscas e desenhos são restritos aos limites geográficos do estado.",
  },
];

const CAMADAS_ITEMS: AccordionEntry[] = [
  {
    title: "Como ativo uma camada no mapa?",
    content:
      "Clique no painel lateral e acesse a aba Camadas. As camadas estão organizadas por categoria — selecione as que deseja visualizar. A camada aparece no mapa imediatamente.",
  },
  {
    title: "Posso filtrar as camadas de Linhas de Transmissão por tensão?",
    content:
      "Sim. Ao selecionar camadas de Linhas de Transmissão ou Subestações, aparece um submenu com opções de filtragem por tensão (69 kV, 138 kV, 230 kV, etc.).",
  },
  {
    title: "Como limpo todas as camadas ativas de uma vez?",
    content:
      'No painel de Camadas, há um botão "Limpar selecionados" que remove todas as camadas ativas simultaneamente.',
  },
  {
    title: "Posso adicionar meus próprios dados ao mapa?",
    content:
      'Sim. No painel de Camadas, clique em "Adicionar Camada" e faça upload do seu arquivo. Os formatos suportados são: GeoJSON, KML e Shapefile (.shp).',
  },
  {
    title: "Por que uma camada não aparece no mapa mesmo estando selecionada?",
    content:
      "Verifique se você possui permissão de acesso à camada — o acesso é controlado pelo seu perfil de usuário. Caso necessite de acesso, entre em contato com o administrador.",
  },
];

const FERRAMENTAS_ITEMS: AccordionEntry[] = [
  {
    title: "Como meço a distância entre dois pontos?",
    content:
      "Abra o menu de ferramentas, selecione Medir Distância e clique nos pontos no mapa. A distância é calculada automaticamente em metros ou quilômetros.",
  },
  {
    title: "Como calculo a área de uma região?",
    content:
      "Selecione a ferramenta Medir Área, desenhe o polígono clicando nos vértices da região desejada e feche o polígono. A área é exibida automaticamente com opções de conversão de unidade (m², km², hectares).",
  },
  {
    title: "Como desenho uma área para análise solar ou eólica?",
    content:
      "Use a ferramenta Desenhar no menu de ferramentas. Após fechar o polígono, o painel de estatísticas exibe automaticamente os dados de potencial solar e eólico para aquela área.",
  },
  {
    title: "Posso alterar a unidade de medida de um desenho depois de criado?",
    content:
      "Sim. No menu de ferramentas, acesse a lista de desenhos, clique no desenho desejado e altere a unidade de medida. O valor é atualizado automaticamente.",
  },
  {
    title: "Como exporto um desenho que fiz?",
    content:
      "Na lista de desenhos no menu de ferramentas, cada item tem uma opção de download em formato GeoJSON.",
  },
];

const SOLAR_EOLICA_ITEMS: AccordionEntry[] = [
  {
    title: "O que é o Painel de Estatísticas?",
    content:
      "É um painel que exibe dados de potencial de energia solar e eólica para uma área selecionada no mapa. Ele é ativado automaticamente ao desenhar uma área ou ao ativar camadas de mapa solar/eólico.",
  },
  {
    title: "Como vejo o potencial solar de uma região?",
    content:
      "Ative a camada Potencial Solar (PVOUT) no painel de camadas e depois desenhe uma área no mapa. O painel de estatísticas exibirá a irradiação mensal, média anual e a distribuição de irradiação da área.",
  },
  {
    title: "O que significa cada cenário na aba Cenários?",
    content:
      "Os cenários representam diferentes hipóteses de restrição para análise energética: Teórico (sem restrições), Geográfico (considera limites geográficos), Técnico (inclui restrições técnicas de infraestrutura), Econômico I/II/III (considera viabilidade econômica em diferentes graus) e Híbrido (combina geração solar e eólica na mesma análise).",
  },
  {
    title: "Como seleciono a altura de medição do vento?",
    content:
      "Quando a camada Velocidade do Vento está ativa, um controle aparece no canto do mapa com as opções: 10m, 50m, 150m e 200m.",
  },
  {
    title: "O painel eólico mostra dados por direção do vento?",
    content:
      "Sim. A Rosa dos Ventos exibe a distribuição direcional do vento na área selecionada.",
  },
];

const RELATORIOS_ITEMS: AccordionEntry[] = [
  {
    title: "Como gero um relatório em PDF?",
    content:
      "Clique no botão de relatório na barra de ações do mapa. Preencha o nome e a descrição do projeto, escolha o que incluir (mapa, estatísticas, legendas) e clique em gerar. O PDF é gerado com os dados visíveis no momento.",
  },
  {
    title: "O relatório inclui os gráficos de análise?",
    content:
      'Sim, desde que a opção "Incluir Estatísticas" esteja marcada e haja uma área analisada com dados de energia solar ou eólica.',
  },
  {
    title: "Posso pré-visualizar o relatório antes de imprimir?",
    content:
      "Sim. Há uma pré-visualização disponível antes de confirmar a geração do PDF.",
  },
];

const IMPORTACAO_ITEMS: AccordionEntry[] = [
  {
    title: "Quais formatos de arquivo posso importar?",
    content:
      "A plataforma suporta: GeoJSON (.geojson, .json), KML (.kml) e Shapefile (.shp).",
  },
  {
    title: "Como gerencio as camadas que importei?",
    content:
      "As camadas importadas aparecem em uma seção separada no painel lateral. Você pode alternar a visibilidade de cada camada individualmente, dar zoom na extensão da camada ou removê-la.",
  },
  {
    title: "Por que meu arquivo não carregou?",
    content:
      "Verifique se o arquivo está em um dos formatos suportados e se a estrutura é válida. GeoJSON, por exemplo, deve conter um campo features válido. A plataforma exibe mensagens de erro específicas para cada tipo de problema.",
  },
];

const CONTA_ITEMS: AccordionEntry[] = [
  {
    title: "Como altero meu nome ou e-mail?",
    content:
      "Entre em contato com o administrador da plataforma para solicitar a alteração de dados cadastrais como nome, sobrenome ou e-mail.",
  },
  {
    title: "Quais são os tipos de perfil de usuário?",
    content:
      "Existem três perfis: Viewer (visualizador básico), Specialist (acesso expandido com análises) e Admin (gerenciamento completo da plataforma).",
  },
  {
    title: "Como recupero minha senha?",
    content:
      'Na tela de login, clique em "Recuperar senha" e informe seu e-mail. Você receberá as instruções para redefinição.',
  },
];

const ADMIN_ITEMS: AccordionEntry[] = [
  {
    title: "Como cadastro um novo usuário?",
    content:
      "Acesse o painel Admin e vá até a aba Usuários. Clique em adicionar usuário, preencha os dados e atribua o perfil adequado.",
  },
  {
    title: "Como controlo quais camadas cada usuário pode visualizar?",
    content:
      "Na aba Camadas do painel Admin, é possível configurar as permissões de acesso a cada camada por perfil de usuário (viewer, specialist, admin).",
  },
];

function Accordion({ items }: { items: AccordionEntry[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <AccordionList>
      {items.map((item, i) => {
        const isOpen = openIndex === i;
        return (
          <AccordionItem key={item.title}>
            <AccordionHeader
              onClick={() => setOpenIndex(isOpen ? null : i)}
              aria-expanded={isOpen}
            >
              <AccordionItemTitle>{item.title}</AccordionItemTitle>
              {isOpen ? <ChevronUp size={22} /> : <ChevronDown size={22} />}
            </AccordionHeader>
            {isOpen && item.content && (
              <AccordionBody>{item.content}</AccordionBody>
            )}
          </AccordionItem>
        );
      })}
    </AccordionList>
  );
}

const SECTIONS = [
  {
    title: "Mapa e Navegação",
    description: "Saiba como navegar pelo mapa, realizar buscas e interagir com a plataforma.",
    items: MAPA_ITEMS,
  },
  {
    title: "Camadas",
    description: "Como ativar, filtrar e gerenciar as camadas de dados no mapa.",
    items: CAMADAS_ITEMS,
  },
  {
    title: "Ferramentas de Análise",
    description: "Utilize as ferramentas de medição e desenho para análise de dados geoespaciais.",
    items: FERRAMENTAS_ITEMS,
  },
  {
    title: "Análise Solar e Eólica",
    description: "Entenda os painéis de estatísticas e os dados de potencial energético.",
    items: SOLAR_EOLICA_ITEMS,
  },
  {
    title: "Relatórios",
    description: "Gere e configure relatórios em PDF com os dados visualizados.",
    items: RELATORIOS_ITEMS,
  },
  {
    title: "Importação de Dados",
    description: "Importe seus próprios arquivos geoespaciais para a plataforma.",
    items: IMPORTACAO_ITEMS,
  },
  {
    title: "Conta e Perfil",
    description: "Gerencie suas informações pessoais e configurações de conta.",
    items: CONTA_ITEMS,
  },
  {
    title: "Administração",
    description: "Funcionalidades disponíveis para perfis de administrador.",
    items: ADMIN_ITEMS,
  },
];

export function PerguntasFrequentes() {
  return (
    <PageContainer>
      <PageTitle>Perguntas Frequentes</PageTitle>
      <SectionsContainer>
        {SECTIONS.map((section) => (
          <Section key={section.title}>
            <SectionLabel>
              <SectionTitle>{section.title}</SectionTitle>
              <SectionDescription>{section.description}</SectionDescription>
            </SectionLabel>
            <Accordion items={section.items} />
          </Section>
        ))}
      </SectionsContainer>
    </PageContainer>
  );
}
